/**
 * M1.1 session-control panel (mobile skeleton, default OFF).
 *
 * Renders in the session detail page. Control is disabled unless the daemon
 * advertises `SessionSummary.control.enabled` for a DSH session, the live relay
 * is connected, the device is paired and online. Every action needs a dedicated
 * confirmation (not the approval scopes) and is signed per command; there is no
 * batch/allow-always/terminate/delete UI. The daemon (M1.1) still rejects
 * commands until real ACP wiring exists.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  SESSION_COMMAND_TEXT_MAX_BYTES,
  type SessionCommandAction,
  type SessionCommandMode,
} from '@luminis/protocol';
import { nowSeconds } from '../lib/id';
import {
  SESSION_COMMAND_MODE_DEFAULT,
  SESSION_COMMAND_MODE_DESCRIPTIONS,
  SESSION_COMMAND_MODE_LABELS,
  SESSION_COMMAND_STATE_LABELS,
  SESSION_CONTROL_RISK_WARNINGS,
  SESSION_CONTROL_UI_MODES,
  resolveSessionCommandMode,
  sessionCommandPresentation,
  sessionControlAvailability,
  sessionControlHidesActions,
  sessionControlOffersModeChoice,
  utf8ByteLength,
  validateCommandText,
} from '../lib/session-control';
import type { SessionView } from '../lib/types';
import { useApp } from '../state/AppState';
import { Icon } from './Icon';

const MESSAGE_EDITOR_MAX_PREVIEW = 500;
const CLIENT_TIMEOUT_MS = 30_000;

const TERMINAL_STATES = new Set(['succeeded', 'failed', 'timeout', 'rejected', 'unknown', 'error']);

function CapabilityBadge({ label, enabled }: { label: string; enabled: boolean }) {
  return (
    <span
      className={`rounded-full border px-2 py-0.5 text-[10px] ${
        enabled
          ? 'border-emerald-400/30 bg-emerald-400/10 text-emerald-200'
          : 'border-white/10 bg-white/5 text-slate-500'
      }`}
    >
      {label}
    </span>
  );
}

function ConfirmControlDialog({
  action,
  text,
  mode,
  busy,
  error,
  onCancel,
  onConfirm,
}: {
  action: SessionCommandAction;
  text: string;
  /** Selected message admission mode; undefined when the session offers no choice. */
  mode?: SessionCommandMode;
  busy: boolean;
  error: string | null;
  onCancel(): void;
  onConfirm(): void;
}) {
  const actionLabel = action === 'message' ? '发送消息' : '中断会话';
  const preview =
    text.length > MESSAGE_EDITOR_MAX_PREVIEW
      ? `${text.slice(0, MESSAGE_EDITOR_MAX_PREVIEW)}…`
      : text;
  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/70 p-4 sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-label="会话控制确认"
    >
      <section className="w-full max-w-md rounded-2xl border border-white/10 bg-[#0f1629] p-4 shadow-2xl">
        <h3 className="text-sm font-semibold text-slate-100">确认{actionLabel}？</h3>
        <p className="mt-1 text-[11px] text-slate-500">
          这是高风险控制操作，需逐次确认并签名（不能批量或永久允许）。
        </p>
        {action === 'message' ? (
          <div className="mt-2 rounded-xl border border-white/5 bg-black/25 px-3 py-2 text-xs text-slate-200">
            {mode !== undefined ? (
              <p
                data-testid="session-control-confirm-mode"
                className="mb-1 rounded-lg border border-indigo-400/25 bg-indigo-400/10 px-2 py-1 text-[10px] text-indigo-100"
              >
                发送方式：<span className="font-medium">{SESSION_COMMAND_MODE_LABELS[mode]}</span>
                <span className="ml-1 text-indigo-200/80">
                  （{SESSION_COMMAND_MODE_DESCRIPTIONS[mode]}）
                </span>
              </p>
            ) : null}
            <p className="whitespace-pre-wrap break-words">{preview}</p>
            <p className="mt-1 text-[10px] text-slate-500">
              {utf8ByteLength(text)} / {SESSION_COMMAND_TEXT_MAX_BYTES} UTF-8 字节
            </p>
          </div>
        ) : null}
        <ul className="mt-2 list-disc space-y-1 pl-4 text-[11px] leading-relaxed text-amber-200/90">
          {SESSION_CONTROL_RISK_WARNINGS.map((warning) => (
            <li key={warning}>{warning}</li>
          ))}
        </ul>
        {error ? <p className="mt-2 text-[11px] text-rose-300">{error}</p> : null}
        <div className="mt-3 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-xs text-slate-300 disabled:opacity-40"
          >
            取消
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className="rounded-lg border border-amber-400/40 bg-amber-400/15 px-3 py-1.5 text-xs font-medium text-amber-100 disabled:opacity-40"
          >
            {busy ? '签名并发送中…' : '确认并签名发送'}
          </button>
        </div>
      </section>
    </div>
  );
}

export function SessionControlPanel({ session }: { session: SessionView }) {
  const {
    settings,
    paired,
    relayPlan,
    network,
    sessionCommands,
    sendSessionCommand,
    clearSessionCommand,
  } = useApp();
  const availability = useMemo(
    () =>
      sessionControlAvailability(session, {
        demoMode: settings.demoMode,
        paired: Boolean(paired),
        online: network.online,
        relayReady: relayPlan.enabled && network.relayConnected,
      }),
    [session, settings.demoMode, paired, network.online, network.relayConnected, relayPlan.enabled],
  );
  const canMessage = availability.available && availability.actions.includes('message');
  const canInterrupt = availability.available && availability.actions.includes('interrupt');
  // Per-command steer/queue choice is offered ONLY for message commands on
  // sessions where the daemon advertises both modes (desktop bridge). Every
  // other session keeps the pre-mode behaviour: selector hidden, no `mode` sent.
  const canChooseMessageMode = canMessage && sessionControlOffersModeChoice(session.control);
  // Unauthorized sessions must not show any send/interrupt affordance at all.
  const hideActions = sessionControlHidesActions(availability);

  const [editor, setEditor] = useState<'none' | 'message'>('none');
  const [text, setText] = useState('');
  const [messageMode, setMessageMode] = useState<SessionCommandMode>(SESSION_COMMAND_MODE_DEFAULT);
  const [confirming, setConfirming] = useState<{
    action: SessionCommandAction;
    text: string;
    /** Explicit mode only when the selector was visible when confirming. */
    mode?: SessionCommandMode;
  } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastCommandId, setLastCommandId] = useState<string | null>(null);
  const [clientTimedOut, setClientTimedOut] = useState(false);

  const command = lastCommandId ? sessionCommands[lastCommandId] : undefined;
  const isTerminal = command !== undefined && TERMINAL_STATES.has(command.state);
  const bytes = utf8ByteLength(text);
  const textValidation = validateCommandText('message', text);

  useEffect(() => {
    if (!lastCommandId || isTerminal) {
      setClientTimedOut(false);
      return undefined;
    }
    const timer = setTimeout(() => setClientTimedOut(true), CLIENT_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [lastCommandId, isTerminal, command?.state]);

  const openMessageEditor = (): void => {
    if (!canMessage) return;
    // Safe default on every open: steer (non-preemptive).
    setMessageMode(SESSION_COMMAND_MODE_DEFAULT);
    setEditor('message');
    setError(null);
  };

  const requestConfirm = (action: SessionCommandAction, value: string): void => {
    setError(null);
    if (action === 'message') {
      const validation = validateCommandText('message', value);
      if (!validation.ok) {
        setError(validation.message);
        return;
      }
      // Resolve BEFORE signing: the chosen mode is part of the Ed25519
      // transcript, and it is omitted entirely when the session advertises no
      // per-command choice (daemon default applies).
      setConfirming({
        action,
        text: value,
        ...(canChooseMessageMode
          ? { mode: resolveSessionCommandMode(session.control, messageMode) }
          : {}),
      });
      return;
    }
    setConfirming({ action, text: '' });
  };

  const confirm = async (): Promise<void> => {
    if (!confirming || busy) return;
    setBusy(true);
    setError(null);
    const result = await sendSessionCommand({
      session,
      action: confirming.action,
      text: confirming.text,
      ...(confirming.mode !== undefined ? { mode: confirming.mode } : {}),
    });
    setBusy(false);
    if (result.ok && result.command_id) {
      setLastCommandId(result.command_id);
      setClientTimedOut(false);
      setConfirming(null);
      setEditor('none');
      setText('');
      setMessageMode(SESSION_COMMAND_MODE_DEFAULT);
    } else {
      setError(result.error ?? '发送失败');
    }
  };

  const presentation =
    command !== undefined
      ? sessionCommandPresentation(command, { timedOut: clientTimedOut && !isTerminal })
      : undefined;
  const toneClass =
    presentation?.tone === 'success'
      ? 'text-emerald-300'
      : presentation?.tone === 'failure'
        ? 'text-rose-300'
        : presentation?.tone === 'pending'
          ? 'text-amber-300'
          : 'text-slate-100';

  return (
    <section className="mt-4 rounded-2xl border border-white/10 bg-[#0f1629] p-3.5 text-xs">
      <div className="flex items-center justify-between gap-2">
        <h2 className="flex items-center gap-1.5 text-xs font-medium text-slate-300">
          <Icon name="shield" className="h-3.5 w-3.5 text-amber-300" />
          会话控制
          <span className="rounded-full border border-amber-400/30 bg-amber-400/10 px-1.5 py-0.5 text-[9px] text-amber-200">
            受控
          </span>
        </h2>
        <div className="flex items-center gap-1.5">
          <CapabilityBadge label="可发送" enabled={canMessage} />
          <CapabilityBadge label="可中断" enabled={canInterrupt} />
        </div>
      </div>

      {!availability.available ? (
        <p
          data-testid={hideActions ? 'session-control-unauthorized' : 'session-control-unavailable'}
          className={
            hideActions
              ? 'mt-2 rounded-xl border border-amber-400/30 bg-amber-400/10 px-3 py-2 text-[11px] leading-relaxed text-amber-200'
              : 'mt-2 rounded-xl border border-white/5 bg-white/[0.02] px-3 py-2 text-[11px] leading-relaxed text-slate-500'
          }
        >
          {hideActions
            ? `未授权控制：${availability.reason}。手机不会显示发送控件或尝试任何命令。`
            : `控制不可用：${availability.reason ?? '未启用'}。默认关闭，不会自动发送任何命令。`}
        </p>
      ) : (
        <p className="mt-2 text-[11px] leading-relaxed text-slate-500">
          daemon 已为该会话声明控制能力；每条命令都需要单独确认并签名。
        </p>
      )}

      {hideActions ? null : (
        <div className="mt-2 flex flex-wrap gap-2">
          <button
            type="button"
            data-control-action="message"
            disabled={!canMessage || busy}
            onClick={openMessageEditor}
            className="rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-xs text-slate-200 disabled:opacity-40"
          >
            发送消息…
          </button>
          <button
            type="button"
            data-control-action="interrupt"
            disabled={!canInterrupt || busy}
            onClick={() => requestConfirm('interrupt', '')}
            className="rounded-lg border border-amber-400/30 bg-amber-400/10 px-3 py-1.5 text-xs text-amber-100 disabled:opacity-40"
          >
            中断会话…
          </button>
        </div>
      )}

      {editor === 'message' && canMessage ? (
        <div className="mt-2 rounded-xl border border-white/5 bg-black/25 p-2.5">
          {canChooseMessageMode ? (
            <div className="mb-2" data-testid="session-control-mode-selector">
              <div
                className="flex items-center justify-between gap-2"
                role="group"
                aria-label="发送方式"
              >
                <span className="text-[10px] text-slate-500">发送方式（随命令签名）</span>
                <span className="flex gap-1">
                  {SESSION_CONTROL_UI_MODES.map((option) => {
                    const selected = messageMode === option;
                    return (
                      <button
                        key={option}
                        type="button"
                        data-control-mode={option}
                        aria-pressed={selected}
                        onClick={() => setMessageMode(option)}
                        className={`rounded-lg border px-2 py-1 text-[11px] ${
                          selected
                            ? 'border-indigo-400/60 bg-indigo-400/20 text-indigo-100'
                            : 'border-white/10 bg-white/5 text-slate-300'
                        }`}
                      >
                        {SESSION_COMMAND_MODE_LABELS[option]}
                      </button>
                    );
                  })}
                </span>
              </div>
              <p className="mt-1 text-[10px] leading-relaxed text-slate-500">
                实时插话：非抢占，不能停止正在运行的工具；排队：下一个 turn
                才生效。发送方式会随命令一起签名。
              </p>
            </div>
          ) : null}
          <textarea
            value={text}
            onChange={(event) => setText(event.target.value)}
            placeholder="输入要发送到该会话的消息（不会自动重发）"
            rows={3}
            className="w-full resize-y rounded-lg border border-white/10 bg-[#0f1629] px-2.5 py-2 text-xs text-slate-100 outline-none focus:border-indigo-400/60"
          />
          <div className="mt-1 flex items-center justify-between gap-2 text-[10px]">
            <span
              className={
                bytes > SESSION_COMMAND_TEXT_MAX_BYTES ? 'text-rose-300' : 'text-slate-500'
              }
            >
              {bytes} / {SESSION_COMMAND_TEXT_MAX_BYTES} UTF-8 字节 · {Array.from(text).length} 字符
            </span>
            <span className="flex gap-2">
              <button
                type="button"
                onClick={() => {
                  setEditor('none');
                  setError(null);
                }}
                className="rounded-lg border border-white/10 bg-white/5 px-2.5 py-1 text-[11px] text-slate-300"
              >
                取消
              </button>
              <button
                type="button"
                disabled={!textValidation.ok || busy}
                onClick={() => requestConfirm('message', text)}
                className="rounded-lg border border-indigo-400/40 bg-indigo-400/15 px-2.5 py-1 text-[11px] text-indigo-100 disabled:opacity-40"
              >
                下一步确认
              </button>
            </span>
          </div>
          {!textValidation.ok && text.trim().length > 0 ? (
            <p className="mt-1 text-[10px] text-rose-300">{textValidation.message}</p>
          ) : null}
        </div>
      ) : null}

      {error ? <p className="mt-2 text-[11px] text-rose-300">{error}</p> : null}

      {lastCommandId ? (
        <div className="mt-2 rounded-xl border border-white/5 bg-black/25 px-3 py-2 text-[11px]">
          <p className="flex flex-wrap items-center gap-1.5 text-slate-300">
            <span>命令状态：</span>
            <span className={`font-medium ${toneClass}`}>
              {presentation?.label ?? SESSION_COMMAND_STATE_LABELS.sent}
            </span>
            <span className="font-mono text-[10px] text-slate-600">{lastCommandId}</span>
          </p>
          <p className="mt-1 text-[10px] text-slate-500">
            命令内容不会写入日志；失败原因由 daemon 脱敏后回传。
          </p>
          {presentation?.message ? (
            <p
              className={`mt-1 break-words text-[11px] leading-relaxed ${
                presentation.ownershipConflict
                  ? 'rounded-lg border border-amber-400/30 bg-amber-400/10 px-2 py-1.5 text-amber-200'
                  : presentation.tone === 'failure'
                    ? 'text-rose-200'
                    : 'text-slate-300'
              }`}
            >
              {presentation.message}
            </p>
          ) : null}
          {presentation?.code ? (
            <p className="mt-1 font-mono text-[10px] text-slate-500">code: {presentation.code}</p>
          ) : null}
          <button
            type="button"
            onClick={() => {
              clearSessionCommand(lastCommandId);
              setLastCommandId(null);
              setClientTimedOut(false);
              setError(null);
            }}
            className="mt-1 rounded-lg border border-white/10 bg-white/5 px-2.5 py-1 text-[10px] text-slate-400"
          >
            清除状态
          </button>
        </div>
      ) : null}

      <p className="mt-2 text-[10px] leading-relaxed text-slate-600">
        仅显示 daemon 广告的能力；不提供批量、永久允许、terminate / create / delete。 daemon
        未启用控制时会安全拒绝。
      </p>

      {confirming ? (
        <ConfirmControlDialog
          action={confirming.action}
          text={confirming.text}
          {...(confirming.mode !== undefined ? { mode: confirming.mode } : {})}
          busy={busy}
          error={error}
          onCancel={() => {
            if (!busy) setConfirming(null);
          }}
          onConfirm={() => void confirm()}
        />
      ) : null}
    </section>
  );
}
